import { AlertTriangle } from 'lucide-react'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipPortal, cn } from '@atrvd/saas-ui'

export interface DataWarningIconProps {
  /** The warning text; rendered in the tooltip AND used to build the accessible name. */
  message: string
  /**
   * Overrides the accessible name when the plain `Warning: ${message}` is
   * ambiguous (the Cash Position grid renders many icons per row and must
   * name its column).
   */
  label?: string
  /** Extra classes for the icon itself — the grid uses a smaller size than the tiles. */
  className?: string
  /** Tooltip side; the grid needs "bottom" on its top-most row. Defaults to "top". */
  side?: 'top' | 'bottom'
}

/**
 * "Data missing/stale for today" warning affordance.
 *
 * DCP-218 introduced this amber triangle inside the Banking balance tiles
 * (`BalanceTile`). DCP-161's dev note asks for the Cash Position Summary
 * table to reuse the *same* affordance rather than re-implement it inline,
 * so both call sites cannot drift apart. This component is that single
 * source of truth: same icon, same colours, same tooltip behaviour, with
 * only size/label/tooltip-side made configurable for the grid's tighter
 * layout.
 *
 * The tooltip's `TooltipContent` (from `@atrvd/saas-ui`) does not wrap
 * itself in a portal. Inside a scrolling container — like the Cash
 * Position grid — an un-portaled tooltip can be clipped by the container's
 * `overflow`. Wrapping it in `TooltipPortal` renders the content at the
 * document body instead, so it is never clipped regardless of which
 * scrollable ancestor it is rendered under. This has no visible effect in
 * the (non-scrolling) balance tiles.
 *
 * `TooltipPortal` comes from `@atrvd/saas-ui`, NOT from
 * `@radix-ui/react-tooltip`. Radix links Root↔Portal through a React context
 * owned by the tooltip module INSTANCE; saas-ui ships Radix as an external
 * import, so this app's own `@radix-ui/react-tooltip` dependency resolves a
 * SECOND physical copy whose `Portal` cannot see the context saas-ui's
 * `Tooltip` created. That threw "`TooltipPortal` must be used within
 * `Tooltip`" at runtime, unmounted the tree and showed a white screen on
 * every page carrying this icon — in the production bundle only, which is why
 * dev and the unit tests stayed green. Going through saas-ui keeps both halves
 * on one instance; an ESLint rule now blocks the direct-Radix route.
 */
export function DataWarningIcon({
  message,
  label,
  className,
  side = 'top',
}: DataWarningIconProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* `tabIndex={0}` is required, not decorative: `asChild` clones this
            <svg> as the trigger, Radix does not inject a tabIndex when it does,
            and an SVG is not focusable by default — so without it the tooltip
            opens on pointer hover ONLY and a keyboard or touch user can never
            read WHY the value is flagged (WCAG 2.1.1). Screen readers still get
            the sentence through aria-label either way. */}
        <AlertTriangle
          className={cn('h-4 w-4 cursor-help text-amber-500', className)}
          role="img"
          tabIndex={0}
          aria-label={label ?? `Warning: ${message}`}
        />
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent side={side} className="max-w-xs">
          <p>{message}</p>
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  )
}
